import { Component, ElementRef, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { PeopleService } from '../../../services/people.service';
import { CpfSharedService } from '../../../services/cpf-shared.service';
import { CommonModule } from '@angular/common';
import { NgxMaskPipe } from 'ngx-mask';
import { Clipboard } from '@angular/cdk/clipboard';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { PanelModule } from 'primeng/panel';
import { ToastModule } from 'primeng/toast';
import { TableModule } from 'primeng/table';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-people-search-component',
  standalone: true,
  providers: [MessageService],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    NgxMaskPipe,
    CardModule,
    ButtonModule,
    InputTextModule,
    PanelModule,
    ToastModule,
    TableModule,
    ProgressSpinnerModule,
    TooltipModule
  ],
  template: `
    <p-toast></p-toast>
    <p-card>
      <ng-template pTemplate="header">
        <div class="card-header">
          <h2><i class="pi pi-users"></i> Consulta de Pessoas</h2>
        </div>
      </ng-template>

      <div class="people-container">
        <form [formGroup]="searchForm" (submit)="onSearch()" class="people-form">
          <!-- Seção: Consulta -->
          <p-panel header="Pesquisa por Nome" [toggleable]="false" styleClass="compact-panel">
            <div class="form-grid">
              <div class="form-row">
                <div class="form-field">
                  <label for="name" class="field-label">Nome da Pessoa*</label>
                  <input
                    #nameInput
                    id="name"
                    pInputText
                    formControlName="name"
                    class="compact-input"
                    placeholder="Digite o nome para pesquisar"
                    maxlength="100"
                    type="text"
                    (input)="onNameInput($event)">
                </div>

                <div class="form-field">
                  <label class="field-label">&nbsp;</label>
                  <div class="button-group">
                    <p-button
                      label="Buscar"
                      type="submit"
                      styleClass="search-button"
                      [loading]="isLoading">
                    </p-button>
                    <p-button
                      *ngIf="searchForm.get('name')?.value"
                      icon="pi pi-times"
                      type="button"
                      (onClick)="clearSearch()"
                      styleClass="clear-button p-button-text">
                    </p-button>
                  </div>
                </div>
              </div>
            </div>
          </p-panel>

          <!-- Loading State -->
          <p-panel *ngIf="isLoading" header="Carregando" [toggleable]="false" styleClass="compact-panel">
            <div class="loading-state">
              <p-progressSpinner styleClass="custom-spinner" strokeWidth="4"></p-progressSpinner>
              <p class="message-text">Buscando pessoas...</p>
            </div>
          </p-panel>

          <!-- Error State -->
          <p-panel *ngIf="errorMessage && !isLoading" header="Erro" [toggleable]="false" styleClass="compact-panel error-panel">
            <div class="error-state">
              <i class="pi pi-exclamation-triangle"></i>
              <span>{{ errorMessage }}</span>
            </div>
          </p-panel>

          <!-- No Results State -->
          <p-panel *ngIf="noResultsFound && !isLoading && !errorMessage" header="Resultado" [toggleable]="false" styleClass="compact-panel">
            <div class="no-data">
              <i class="pi pi-info-circle"></i>
              <span>Nenhuma pessoa encontrada com esse nome.</span>
            </div>
          </p-panel>

          <!-- Results -->
          <p-panel *ngIf="searchResults.length > 0 && !isLoading" [header]="'Resultados (' + searchResults.length + ')'" [toggleable]="false" styleClass="compact-panel">
            <!-- Desktop Table -->
            <div class="table-container">
              <p-table [value]="searchResults" [tableStyle]="{'min-width': '100%'}" styleClass="p-datatable-sm">
                <ng-template pTemplate="header">
                  <tr>
                    <th style="width: 50%">Nome</th>
                    <th style="width: 30%">CPF</th>
                    <th style="width: 20%; text-align: center">Ações</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-person>
                  <tr>
                    <td class="person-name">{{ person.name || 'Nome não informado' }}</td>
                    <td class="person-cpf">{{ person.cpf | mask: '000.000.000-00' }}</td>
                    <td style="text-align: center">
                      <p-button
                        icon="pi pi-copy"
                        styleClass="copy-button p-button-text p-button-rounded p-button-sm"
                        (onClick)="copyToClipboard(person.cpf)"
                        pTooltip="Copiar CPF">
                      </p-button>
                      <p-button
                        icon="pi pi-id-card"
                        styleClass="credential-button p-button-text p-button-rounded p-button-sm"
                        (onClick)="selectCpfForCredential(person.cpf)"
                        pTooltip="Emitir Credencial">
                      </p-button>
                    </td>
                  </tr>
                </ng-template>
              </p-table>
            </div>

            <!-- Mobile Cards -->
            <div class="mobile-results">
              <div *ngFor="let person of searchResults" class="mobile-person-card">
                <div class="mobile-card-content">
                  <div class="mobile-field">
                    <label class="mobile-label">Nome:</label>
                    <span class="mobile-value">{{ person.name || 'Nome não informado' }}</span>
                  </div>
                  <div class="mobile-field">
                    <label class="mobile-label">CPF:</label>
                    <span class="mobile-value">{{ person.cpf | mask: '000.000.000-00' }}</span>
                    <p-button
                      icon="pi pi-copy"
                      styleClass="mobile-copy-button p-button-text p-button-rounded p-button-sm"
                      (onClick)="copyToClipboard(person.cpf)"
                      pTooltip="Copiar CPF">
                    </p-button>
                  </div>
                  <div class="mobile-field mobile-actions">
                    <p-button
                      label="Emitir Credencial"
                      icon="pi pi-id-card"
                      styleClass="mobile-credential-button p-button-sm"
                      (onClick)="selectCpfForCredential(person.cpf)">
                    </p-button>
                  </div>
                  <div class="mobile-field" *ngIf="person.email">
                    <label class="mobile-label">Email:</label>
                    <span class="mobile-value">{{ person.email }}</span>
                  </div>
                  <div class="mobile-field" *ngIf="person.phone">
                    <label class="mobile-label">Telefone:</label>
                    <span class="mobile-value">{{ person.phone | mask: '(00) 00000-0000' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </p-panel>
        </form>
      </div>
    </p-card>
  `,
  styles: [`
    /* Card Header */
    .card-header {
      padding: 16px 20px;
      margin: 0;
      background: rgba(255, 230, 203, 0.1);
      border-bottom: 1px solid #FFE6CB;
    }

    .card-header h2 {
      color: #325565;
      font-weight: 600;
      font-size: 1.5rem;
      margin: 0;
      font-family: "Montserrat", sans-serif;
    }

    /* Container Principal */
    .people-container {
      padding: 10px;
    }

    .people-form {
      width: 100%;
    }

    /* Panels Compactos */
    :host ::ng-deep .compact-panel {
      margin-bottom: 1rem;
    }

    :host ::ng-deep .compact-panel .p-panel-header {
      background: rgba(255, 230, 203, 0.1);
      border: 1px solid #FFE6CB;
      color: #325565;
      font-weight: 600;
      padding: 0.75rem 1rem;
    }

    :host ::ng-deep .compact-panel .p-panel-content {
      padding: 1rem;
      border: 1px solid #FFE6CB;
      border-top: none;
    }

    /* Error Panel */
    :host ::ng-deep .error-panel .p-panel-header {
      background: rgba(220, 53, 69, 0.1);
      border: 1px solid #f5c6cb;
      color: #721c24;
    }

    :host ::ng-deep .error-panel .p-panel-content {
      border: 1px solid #f5c6cb;
      border-top: none;
    }

    /* Grid de Formulário */
    .form-grid {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .form-row {
      display: grid;
      grid-template-columns: 3fr 1fr;
      gap: 1rem;
      align-items: end;
    }

    .form-field {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    .field-label {
      font-weight: 600;
      color: #325565;
      font-size: 0.875rem;
      margin-bottom: 0.25rem;
    }

    /* Inputs Compactos */
    :host ::ng-deep .compact-input {
      height: 2.5rem !important;
      font-size: 0.875rem;
      width: 100%;
    }

    /* Button Group */
    .button-group {
      display: flex;
      gap: 0.5rem;
      align-items: center;
    }

    /* Botão de Busca */
    :host ::ng-deep .search-button {
      background-color: #F15A22 !important;
      border-color: #F15A22 !important;
      color: white !important;
    }

    :host ::ng-deep .search-button:hover {
      background-color: #E55A2B !important;
      border-color: #E55A2B !important;
    }

    /* Botão Limpar */
    :host ::ng-deep .clear-button {
      color: #6c757d !important;
    }

    :host ::ng-deep .clear-button:hover {
      color: #495057 !important;
      background-color: rgba(108, 117, 125, 0.1) !important;
    }

    /* Loading State */
    .loading-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 2rem;
      gap: 1rem;
    }

    :host ::ng-deep .custom-spinner {
      width: 50px;
      height: 50px;
    }

    :host ::ng-deep .custom-spinner .p-progress-spinner-circle {
      stroke: #F15A22;
    }

    .message-text {
      color: #325565;
      font-size: 0.875rem;
      font-weight: 500;
    }

    /* Error State */
    .error-state {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      padding: 1rem;
      color: #721c24;
    }

    .error-state i {
      font-size: 1.5rem;
    }

    /* Mensagem de Nenhum Dado */
    .no-data {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      padding: 2rem;
      color: #6c757d;
      background-color: #f8f9fa;
      border-radius: 8px;
      border: 1px solid #e9ecef;
    }

    .no-data i {
      font-size: 1.5rem;
      color: #F15A22;
    }

    .no-data span {
      font-size: 1rem;
      font-style: italic;
    }

    /* Table Container */
    .table-container {
      display: block;
    }

    /* PrimeNG Table Customization */
    :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
      background: white;
      color: #325565;
      font-weight: 700;
      padding: 0.75rem;
      border-bottom: 2px solid #FFE6CB;
    }

    :host ::ng-deep .p-datatable .p-datatable-tbody > tr {
      transition: background-color 0.2s;
    }

    :host ::ng-deep .p-datatable .p-datatable-tbody > tr:hover {
      background-color: #f8f9fa;
    }

    :host ::ng-deep .p-datatable .p-datatable-tbody > tr > td {
      padding: 0.75rem;
    }

    .person-name {
      font-weight: 500;
      color: #495057;
    }

    .person-cpf {
      font-family: monospace;
      color: #495057;
    }

    /* Botão de Copiar */
    :host ::ng-deep .copy-button {
      color: #F15A22 !important;
    }

    :host ::ng-deep .copy-button:hover {
      background-color: rgba(241, 90, 34, 0.1) !important;
    }

    /* Botão de Credencial */
    :host ::ng-deep .credential-button {
      color: rgb(16, 148, 158) !important;
    }

    :host ::ng-deep .credential-button:hover {
      background-color: rgba(16, 148, 158, 0.1) !important;
    }

    /* Mobile Results (hidden on desktop) */
    .mobile-results {
      display: none;
    }

    /* PrimeNG Card Customization */
    :host ::ng-deep .p-card {
      border: none;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      margin: 20px;
    }

    :host ::ng-deep .p-card .p-card-body {
      padding: 0;
    }

    :host ::ng-deep .p-card .p-card-content {
      padding: 20px;
    }

    /* Responsive Design */
    @media (max-width: 768px) {
      .form-row {
        grid-template-columns: 1fr;
        gap: 0.75rem;
      }

      .button-group {
        width: 100%;
      }

      .card-header h2 {
        font-size: 1.25rem;
      }

      /* Hide table on mobile */
      .table-container {
        display: none;
      }

      /* Show mobile cards on mobile */
      .mobile-results {
        display: block;
      }

      .mobile-person-card {
        background-color: #f8f9fa;
        border: 1px solid #dee2e6;
        border-radius: 8px;
        padding: 16px;
        margin-bottom: 16px;
        transition: box-shadow 0.3s ease;
      }

      .mobile-person-card:hover {
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
      }

      .mobile-field {
        display: flex;
        align-items: center;
        margin-bottom: 12px;
        gap: 8px;
      }

      .mobile-field:last-child {
        margin-bottom: 0;
      }

      .mobile-label {
        font-weight: 600;
        color: #325565;
        min-width: 80px;
        font-size: 0.875rem;
      }

      .mobile-value {
        flex: 1;
        color: #495057;
        font-size: 0.875rem;
      }

      :host ::ng-deep .mobile-copy-button {
        color: #F15A22 !important;
        min-width: auto;
      }

      .mobile-actions {
        justify-content: center;
        margin-top: 8px;
        padding-top: 8px;
        border-top: 1px solid #dee2e6;
      }

      :host ::ng-deep .mobile-credential-button {
        background-color: rgb(16, 148, 158) !important;
        border-color: rgb(16, 148, 158) !important;
        color: white !important;
        width: 100%;
      }

      :host ::ng-deep .mobile-credential-button:hover {
        background-color: rgb(6, 182, 212) !important;
        border-color: rgb(6, 182, 212) !important;
      }
    }
  `]
})
export class PeopleSearchComponentComponent implements OnInit, AfterViewInit {
  @ViewChild('nameInput') nameInputField!: ElementRef;

  searchForm: FormGroup;
  searchResults: any[] = [];
  isLoading = false;
  errorMessage = '';
  noResultsFound = false;

  constructor(
    private fb: FormBuilder,
    private peopleService: PeopleService,
    private messageService: MessageService,
    private clipboard: Clipboard,
    private cpfSharedService: CpfSharedService,
    private router: Router
  ) {
    this.searchForm = this.fb.group({
      name: ['']
    });
  }

  ngOnInit(): void {
    this.setupSearch();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.nameInputField) {
        this.nameInputField.nativeElement.focus();
      }
    }, 100);
  }

  private setupSearch(): void {
    this.searchForm.get('name')?.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((name: string) => {
          this.isLoading = true;
          this.errorMessage = '';
          this.noResultsFound = false;

          if (!name || name.trim().length < 2) {
            this.searchResults = [];
            this.isLoading = false;
            return [];
          }

          return this.peopleService.getPeopleByName(name.trim());
        })
      )
      .subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response && response.data) {
            this.searchResults = response.data;
            this.noResultsFound = this.searchResults.length === 0;

            if (this.noResultsFound) {
              this.showSnackBar('Nenhuma pessoa encontrada com esse nome.');
            }
          } else {
            this.searchResults = [];
            this.noResultsFound = true;
            this.showSnackBar('Nenhuma pessoa encontrada com esse nome.');
          }
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = 'Erro ao buscar pessoas. Tente novamente.';
          this.showSnackBar('Erro ao buscar pessoas. Tente novamente.');
          console.error('Erro na busca:', error);
          this.searchResults = [];
        }
      });
  }

  onSearch(): void {
    const name = this.searchForm.get('name')?.value?.trim();
    if (name && name.length >= 2) {
      this.isLoading = true;
      this.errorMessage = '';

      this.peopleService.getPeopleByName(name).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response && response.data) {
            this.searchResults = response.data;
            this.noResultsFound = this.searchResults.length === 0;

            if (this.noResultsFound) {
              this.showSnackBar('Nenhuma pessoa encontrada com esse nome.');
            }
          } else {
            this.searchResults = [];
            this.noResultsFound = true;
            this.showSnackBar('Nenhuma pessoa encontrada com esse nome.');
          }
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = 'Erro ao buscar pessoas. Tente novamente.';
          this.showSnackBar('Erro ao buscar pessoas. Tente novamente.');
          console.error('Erro na busca:', error);
          this.searchResults = [];
        }
      });
    } else if (name && name.length < 2) {
      this.showSnackBar('Digite pelo menos 2 caracteres para pesquisar.');
    }
  }

  clearSearch(): void {
    this.searchForm.reset();
    this.searchResults = [];
    this.errorMessage = '';
    this.noResultsFound = false;

    // Set focus back to input after clearing
    setTimeout(() => {
      if (this.nameInputField) {
        this.nameInputField.nativeElement.focus();
      }
    }, 0);
  }

  onNameInput(event: any): void {
    const input = event.target as HTMLInputElement;
    const cursorPosition = input.selectionStart || 0;
    const value = input.value;

    // Converte para maiúsculas
    const upperValue = value.toUpperCase();

    // Atualiza o FormControl se o valor mudou
    if (value !== upperValue) {
      this.searchForm.get('name')?.setValue(upperValue, { emitEvent: false });
      
      // Restaura a posição do cursor
      setTimeout(() => {
        input.setSelectionRange(cursorPosition, cursorPosition);
      }, 0);
    }
  }

  private capitalizeWords(text: string): string {
    if (!text) return text;
    
    return text
      .toLowerCase()
      .split(' ')
      .map(word => {
        if (word.length === 0) return word;
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  }

  copyToClipboard(cpf: string): void {
    // Remove qualquer formatação do CPF (pontos e traços)
    const cleanCpf = cpf.replace(/\D/g, '');

    // Copia para a área de transferência
    this.clipboard.copy(cleanCpf);

    // Exibe mensagem de confirmação
    this.showSnackBar('CPF copiado para a área de transferência!');
  }

  private showSnackBar(message: string): void {
    this.messageService.add({
      severity: 'info',
      summary: 'Informação',
      detail: message,
      life: 3000
    });
  }

  selectCpfForCredential(cpf: string): void {
    // Remove qualquer formatação do CPF (pontos e traços)
    const cleanCpf = cpf.replace(/\D/g, '');

    // Define o CPF no serviço compartilhado como beneficiário
    this.cpfSharedService.setRecipientCpf(cleanCpf);
    this.cpfSharedService.setCurrentPerson('recipient');
    this.cpfSharedService.setToggleValue(true);

    // Exibe mensagem de confirmação
    this.showSnackBar('Redirecionando para emissão de credencial...');

    // Navega para a página de emissão
    setTimeout(() => {
      this.router.navigate(['/home/emission']);
    }, 500);
  }
}
